import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Music, Upload, DollarSign, Users, Headphones, ChevronLeft, ChevronRight, Calendar, Disc } from 'lucide-react';
import AuthFooter from '../components/layout/AuthFooter';

const slides = [
  {
    image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
    title: 'FOR ARTISTS',
    subtitle: 'Own Your Music. Keep ~90%.',
    description: 'Upload music tracks ($1.99), videos ($3.59), and manage your catalog. Keep ~90% of every sale.',
    icon: Upload,
    color: '#00CCFF'
  },
  {
    image: 'https://images.pexels.com/photos/164938/pexels-photo-164938.jpeg',
    title: 'FOR PRODUCERS',
    subtitle: 'Sell Your Beats Worldwide',
    description: 'License your beats starting at $54.99 with multiple tiers. Keep ~90% of every sale.',
    icon: Music,
    color: '#ff6b6b'
  },
  {
    image: 'https://images.pexels.com/photos/2111015/pexels-photo-2111015.jpeg',
    title: 'FOR DJs',
    subtitle: 'Book Gigs. Grow Your Brand.',
    description: 'Create your EPK, upload mixes, get discovered by promoters. Set your rates and manage bookings.',
    icon: Disc,
    color: '#e67e22'
  },
  {
    image: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg',
    title: 'FOR PROMOTERS',
    subtitle: 'Find Talent. Host Events.',
    description: 'Post gigs, discover DJs, manage bookings and ticket sales. Advanced analytics dashboard.',
    icon: Calendar,
    color: '#9b59b6'
  },
  {
    image: 'https://images.pexels.com/photos/1389429/pexels-photo-1389429.jpeg',
    title: 'FOR FANS',
    subtitle: 'Discover Underground Heat',
    description: 'Purchase tracks ($1.99), beats (from $54.99), and videos ($3.59). Support artists directly.',
    icon: Headphones,
    color: '#ffd93d'
  }
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where to send the user after a successful login. Defaults to /home.
  // We accept only same-origin relative paths to avoid open-redirect abuse.
  const nextParam = searchParams.get('next') || '';
  const safeNext = nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/home';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Trim email and password to handle accidental whitespace
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    // Retry logic for transient network / 5xx failures.  3 attempts with
    // exponential backoff so a middleware blip is invisible to the user.
    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await login(trimmedEmail, trimmedPassword, requires2FA ? twoFactorCode : null);
        
        // Check if password change is required
        if (result.forcePasswordChange) {
          toast.info('Password change required. Please set a new password.');
          navigate('/change-password?required=true');
        } else {
          toast.success('Welcome back!');
          navigate(safeNext);
        }
        lastError = null;
        break;
      } catch (error) {
        // For errors with a response (server answered), don't retry non-5xx
        if (error.response) {
          const detail = error.response.data?.detail || '';
          const is2FA = error.response.status === 403 && (
            error.response.headers?.['x-2fa-required'] === 'true' ||
            detail.toLowerCase().includes('two-factor')
          );
          if (is2FA) {
            setRequires2FA(true);
            toast.info('Two-factor authentication required');
            lastError = null;
            break;
          } else if (error.response.status === 401 && requires2FA) {
            toast.error('Invalid 2FA code. Please try again.');
            lastError = null;
            break;
          } else if (error.response.status === 401) {
            toast.error(detail || 'Invalid credentials');
            lastError = null;
            break;
          } else if (error.response.status === 400 || error.response.status === 422) {
            toast.error(detail || 'Please check your login details');
            lastError = null;
            break;
          } else if (error.response.status >= 500) {
            // Retry 5xx (including 503 "Request interrupted") with a short
            // backoff so a transient middleware / disconnect blip doesn't
            // stop the user in their tracks. Only after the last attempt
            // do we surface a toast.
            lastError = error;
            if (attempt < 2) {
              await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
              continue;
            }
            toast.error('Server is temporarily busy. Please try again in a moment.');
            lastError = null;
            break;
          } else {
            toast.error(detail || 'Login failed');
            lastError = null;
            break;
          }
        }
        // Network error (no response) - retry with backoff
        lastError = error;
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        }
      }
    }
    
    if (lastError) {
      // Both attempts failed with network errors
      console.error('Login network error after retries:', lastError.message, lastError.code);
      if (lastError.code === 'ECONNABORTED' || lastError.message?.includes('timeout')) {
        toast.error('Request timed out. The server may be busy — please try again in a moment.');
      } else if (lastError.code === 'ERR_NETWORK' || !navigator.onLine) {
        toast.error('Network error. Please check your internet connection and try again.');
      } else {
        toast.error('Unable to reach the server. Please try again in a moment.');
      }
    }
    setLoading(false);
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const slide = slides[currentSlide];
  const SlideIcon = slide.icon;

  return (
    <div className="dark-surface min-h-screen bg-black flex flex-col lg:flex-row">
      {/* Left side - Slideshow */}
      <div className="lg:w-1/2 relative h-[40vh] lg:h-screen overflow-hidden">
        {slides.map((s, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${s.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          </div>
        ))}
        
        {/* Slide Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12 z-10">
          <div 
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 transition-all duration-500"
            style={{ backgroundColor: `${slide.color}20`, border: `1px solid ${slide.color}` }}
          >
            <SlideIcon size={16} style={{ color: slide.color }} />
            <span style={{ color: slide.color }} className="text-sm font-bold tracking-wider">
              {slide.title}
            </span>
          </div>
          
          <h2
            className="glow-h1 glow-h1-rich text-2xl lg:text-4xl font-bold text-white mb-2 transition-all duration-500"
            style={{ '--glow-h1-color': '#00ff88', '--glow-h1-accent': '#00CCFF' }}
            data-testid="login-slide-title"
          >
            {slide.subtitle}
          </h2>
          <p className="text-gray-400 text-sm lg:text-base max-w-md transition-all duration-500">
            {slide.description}
          </p>
          
          {/* Slide Navigation */}
          <div className="flex items-center gap-4 mt-6">
            <button 
              onClick={prevSlide}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
            <div className="flex gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentSlide ? 'w-6 bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
            <button 
              onClick={nextSlide}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </div>
        </div>
        
        {/* Feature Pills — hidden on mobile (≤640px) where the slide
         * content already fills the 40vh slideshow area and these pills
         * end up overlapping the per-slide badge/heading. Shown on sm+
         * where there's enough room above the slide content. */}
        <div className="hidden sm:flex absolute top-6 left-6 right-6 flex-wrap gap-2 z-10">
          <span className="px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-xs text-white border border-white/20">
            <DollarSign size={12} className="inline mr-1" />~90% to Artists
          </span>
          <span className="px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-xs text-white border border-white/20">
            <Users size={12} className="inline mr-1" />Independent Platform
          </span>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 lg:px-16 py-8 lg:py-0 pb-20 lg:pb-0">
        <div className="max-w-sm mx-auto w-full">
          <img 
            src="/onevoice-logo-circular.png" 
            alt="One Voice Distribution"
            className="h-16 w-16 mb-6 object-contain"
            data-testid="login-logo"
          />
          
          <h1 className="text-2xl font-bold text-white mb-1">
            {requires2FA ? 'Verify Identity' : 'Welcome Back'}
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            {requires2FA ? 'Enter your authenticator code' : 'Sign in to continue'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!requires2FA ? (
              <>
                <Input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12"
                  placeholder="Email address"
                />
                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12"
                  placeholder="Password"
                />
              </>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <Shield size={16} className="text-cyan-500" />
                  <span className="text-cyan-500 text-sm">2FA enabled on this account</span>
                </div>
                <Input
                  data-testid="2fa-code-input"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="bg-zinc-900 border-zinc-800 text-white text-center text-2xl tracking-[0.5em] h-14"
                  placeholder="000000"
                  autoFocus
                />
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              id="login-form-submit-btn"
              type="submit"
              disabled={loading || (requires2FA && twoFactorCode.length < 6)}
              className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
            >
              {loading ? 'Signing in...' : requires2FA ? 'Verify' : 'Sign In'}
            </Button>
            
            {requires2FA && (
              <button
                type="button"
                onClick={() => { setRequires2FA(false); setTwoFactorCode(''); }}
                className="w-full text-gray-500 text-sm hover:text-white transition-colors"
              >
                ← Back to login
              </button>
            )}
          </form>

          <div className="mt-4 text-center">
            <Link 
              to="/forgot-password" 
              className="text-gray-500 hover:text-cyan-500 text-sm transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
            <p className="text-gray-500 text-sm">
              New to One Voice?{' '}
              <Link 
                to={nextParam ? `/register?next=${encodeURIComponent(nextParam)}` : '/register'}
                className="text-cyan-500 hover:text-cyan-400 font-medium"
                data-testid="register-link"
              >
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  );
}
